<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';

import ListAttribute from 'dashboard/components-next/CustomAttributes/ListAttribute.vue';
import CheckboxAttribute from 'dashboard/components-next/CustomAttributes/CheckboxAttribute.vue';
import DateAttribute from 'dashboard/components-next/CustomAttributes/DateAttribute.vue';
import MultiEntryAttribute from 'dashboard/components-next/CustomAttributes/MultiEntryAttribute.vue';
import OtherAttribute from 'dashboard/components-next/CustomAttributes/OtherAttribute.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
  },
  isEditingView: {
    type: Boolean,
    default: false,
  },
  expiration: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['expiryUpdated']);

const store = useStore();
const { t } = useI18n();
const route = useRoute();

const showExpiryPicker = ref(false);
const expiryDateInput = ref('');

const formattedExpiry = computed(() => {
  if (!props.expiration) return null;
  return new Date(props.expiration.expires_at).toLocaleDateString();
});

const toggleExpiryPicker = () => {
  showExpiryPicker.value = !showExpiryPicker.value;
  if (showExpiryPicker.value) {
    expiryDateInput.value = props.expiration
      ? new Date(props.expiration.expires_at).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
  }
};

const handleSaveExpiry = async () => {
  if (!expiryDateInput.value) return;
  try {
    const contactId = route.params.contactId;
    const { data } = await ContactAPI.upsertAttributeExpiration(contactId, {
      attribute_key: props.attribute.attributeKey,
      expires_at: new Date(expiryDateInput.value).toISOString(),
      on_expire_actions: { clear_value: true },
    });
    emit('expiryUpdated', {
      attributeKey: props.attribute.attributeKey,
      expiration: data,
    });
    showExpiryPicker.value = false;
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.EXPIRY_SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.EXPIRY_ERROR'));
  }
};

const handleDeleteExpiry = async () => {
  try {
    const contactId = route.params.contactId;
    await ContactAPI.deleteAttributeExpiration(contactId, props.expiration.id);
    emit('expiryUpdated', {
      attributeKey: props.attribute.attributeKey,
      expiration: null,
    });
    showExpiryPicker.value = false;
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.EXPIRY_DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.EXPIRY_ERROR'));
  }
};

const handleDelete = async () => {
  try {
    await store.dispatch('contacts/deleteCustomAttributes', {
      id: route.params.contactId,
      customAttributes: [props.attribute.attributeKey],
    });
    useAlert(
      t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.DELETE_SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.DELETE_ERROR')
    );
  }
};

const handleUpdate = async value => {
  try {
    await store.dispatch('contacts/update', {
      id: route.params.contactId,
      customAttributes: {
        [props.attribute.attributeKey]: value,
      },
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.UPDATE_ERROR')
    );
  }
};

const componentMap = {
  list: ListAttribute,
  checkbox: CheckboxAttribute,
  date: DateAttribute,
  multi_entry: MultiEntryAttribute,
  default: OtherAttribute,
};

const CurrentAttributeComponent = computed(() => {
  return (
    componentMap[props.attribute.attributeDisplayType] || componentMap.default
  );
});
</script>

<template>
  <div class="flex flex-col w-full gap-1">
    <div
      class="grid grid-cols-[140px,1fr] group/attribute items-center w-full gap-2"
      :class="isEditingView ? 'min-h-10' : 'min-h-11'"
    >
      <div class="flex items-center truncate">
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ attribute.attributeDisplayName }}
        </span>
      </div>

      <component
        :is="CurrentAttributeComponent"
        :attribute="attribute"
        :is-editing-view="isEditingView"
        @update="handleUpdate"
        @delete="handleDelete"
      />
    </div>

    <!-- Fila de vencimiento (solo en modo edición y cuando hay valor) -->
    <div v-if="isEditingView" class="ltr:pl-[152px] rtl:pr-[152px]">
      <button
        v-if="!showExpiryPicker"
        class="text-xs flex items-center gap-1 hover:underline"
        :class="formattedExpiry ? 'text-n-amber-9' : 'text-n-slate-9'"
        @click.stop="toggleExpiryPicker"
      >
        <span class="i-lucide-clock size-3" />
        <span>{{
          formattedExpiry
            ? $t('CUSTOM_ATTRIBUTES.EXPIRY.EXPIRES_ON', {
                date: formattedExpiry,
              })
            : $t('CUSTOM_ATTRIBUTES.EXPIRY.ADD')
        }}</span>
      </button>

      <div v-else class="flex items-center gap-2">
        <span class="i-lucide-clock size-3.5 text-n-slate-10 flex-shrink-0" />
        <input
          v-model="expiryDateInput"
          type="date"
          class="flex-1 h-7 px-2 text-xs rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border border-n-slate-6 outline-none"
        />
        <button
          class="flex-shrink-0 flex items-center justify-center size-6 rounded bg-n-blue-9 text-white hover:bg-n-blue-10"
          @click="handleSaveExpiry"
        >
          <span class="i-lucide-check size-3" />
        </button>
        <button
          v-if="expiration"
          class="flex-shrink-0 flex items-center justify-center size-6 rounded bg-n-ruby-3 text-n-ruby-9 hover:bg-n-ruby-4"
          @click="handleDeleteExpiry"
        >
          <span class="i-lucide-x size-3" />
        </button>
        <button
          class="flex-shrink-0 flex items-center justify-center size-6 rounded text-n-slate-10 hover:bg-n-alpha-black2"
          @click="showExpiryPicker = false"
        >
          <span class="i-lucide-chevron-up size-3" />
        </button>
      </div>
    </div>
  </div>
</template>
